/**
 * Rules Engine Component
 * Main component for parameter-driven compliance validation
 */

import React, { useState } from "react";
import { useViewerStore } from "@/store/viewerStore";
import { useRulesStore } from "@/store/rulesStore";
import { RulesList } from "./RulesList";
import { CustomRulesList } from "./CustomRulesList";
import { ValidationReportDisplay } from "./ValidationReportDisplay";
import { CustomerDemandsForm } from "./CustomerDemandsForm";

interface RulesEngineProps {
  onValidationComplete?: () => void;
}

export function RulesEngine({ onValidationComplete }: RulesEngineProps = {}) {
  const { viewer, isModelLoaded } = useViewerStore();
  const {
    ruleInstances,
    customRuleInstances,
    validationResults,
    validationSummary,
    isValidating,
    runValidation,
    clearValidationResults,
    getActiveRules,
    getActiveCustomRules,
  } = useRulesStore();

  const [error, setError] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<
    "regulations" | "custom" | "customer-demands"
  >("regulations");

  const handleRunValidation = async () => {
    if (!viewer) {
      setError("Viewer not available");
      return;
    }

    const activeRules = getActiveRules();
    const activeCustomRules = getActiveCustomRules();
    const totalActiveRules = activeRules.length + activeCustomRules.length;

    if (totalActiveRules === 0) {
      setError("Please enable at least one rule");
      return;
    }

    setError(null);
    clearValidationResults();

    try {
      await runValidation();

      // Call parent to switch to Risk Panel when validation is complete
      if (onValidationComplete) {
        onValidationComplete();
      }
    } catch (err) {
      setError("Failed to run validation");
    }
  };

  const handleClearResults = () => {
    clearValidationResults();
    setError(null);
  };

  // Export functionality moved to Risk Panel

  const activeRegulationsCount = ruleInstances.filter((r) => r.isActive).length;
  const activeCustomRulesCount = customRuleInstances.filter(
    (r) => r.isActive
  ).length;
  const totalActiveRulesCount = activeRegulationsCount + activeCustomRulesCount;

  if (!isModelLoaded) {
    return (
      <div className="animate-fade-in p-4">
        <div className="text-center text-custom-grey">
          <p>Please load a model first to run compliance checks</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-custom-navy">
        Select Rules - Prevent rework
      </h2>

      {/* Sub-tabs for Regulations and Custom Rules */}
      <div className="mb-6 border-b border-custom-light-200">
        <div className="flex gap-2">
          <button
            className={`px-4 py-2 text-sm font-medium transition-colors rounded-t-lg ${
              activeSubTab === "regulations"
                ? "bg-custom-navy text-white"
                : "bg-custom-light-100 text-custom-grey hover:bg-custom-light-200"
            }`}
            onClick={() => setActiveSubTab("regulations")}
          >
            Regulations
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium transition-colors rounded-t-lg ${
              activeSubTab === "custom"
                ? "bg-custom-navy text-white"
                : "bg-custom-light-100 text-custom-grey hover:bg-custom-light-200"
            }`}
            onClick={() => setActiveSubTab("custom")}
          >
            Custom Rules (mockup)
            {activeCustomRulesCount > 0 && (
              <span className="ml-2 text-xs opacity-75">
                ({activeCustomRulesCount} active)
              </span>
            )}
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium transition-colors rounded-t-lg ${
              activeSubTab === "customer-demands"
                ? "bg-custom-navy text-white"
                : "bg-custom-light-100 text-custom-grey hover:bg-custom-light-200"
            }`}
            onClick={() => setActiveSubTab("customer-demands")}
          >
          Customer Demands (mockup)
          </button>
        </div>
      </div>

      {/* Error Bar */}
      {error && (
        <div className="mb-4 p-3 rounded-lg border bg-red-50 border-red-200">
          <span className="text-sm text-red-600">Error: {error}</span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 mb-6">
        <button
          className="btn btn-primary flex-1"
          onClick={handleRunValidation}
          disabled={isValidating || totalActiveRulesCount === 0}
        >
          Run Validation
        </button>
        {validationResults.length > 0 && (
          <>
            <button
              className="btn btn-secondary flex-1"
              onClick={handleClearResults}
              disabled={isValidating}
            >
              Clear Results
            </button>
          </>
        )}
      </div>

      {/* Configuration - Regulations, Custom Rules, or Customer Demands */}
      <div className="space-y-4">
        {activeSubTab === "regulations" ? (
          <ValidationReportDisplay />
        ) : activeSubTab === "custom" ? (
          <CustomRulesList />
        ) : (
          <CustomerDemandsForm />
        )}
      </div>
    </div>
  );
}
